import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

export interface SummaryElement {
  CBC_SUMMARY_ELEMENT: string | null;
  CBC_SUMMARY_VALUE: string;
  USER_ENTRY?: boolean;
  USER_ENTRY_VALUE?: string;
  [key: string]: unknown;
}

export interface Jurisdiction {
  COUNTRY_ID: string;
  COUNTRY: string;
  id?: string;
  [key: string]: unknown;
}

export interface TaxYear {
  label: string;
  value: string;
  scenario: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class CbcSchdATbl3Service {
  constructor(private http: HttpClient) {}

  private buildParams(
    globalService: any,
    filterParams: any,
    ssoId: string,
    extra: Record<string, string | number | null | undefined> = {}
  ): HttpParams {
    const g = globalService || {};
    const f = filterParams || {};
    let p = new HttpParams();
    if (ssoId) p = p.set('sso_id', ssoId);
    const taxYear = f.tax_year != null ? f.tax_year : g.tax_year;
    if (taxYear != null) p = p.set('tax_year', String(taxYear));
    const scenario = f.scenario != null ? f.scenario : g.scenario;
    if (scenario != null) p = p.set('scenario', String(scenario));
    const jcdKey = f.jcd_key != null ? f.jcd_key : g.jcd_key;
    if (jcdKey != null) p = p.set('jcd_key', String(jcdKey));
    Object.keys(extra).forEach((k) => {
      const v = extra[k];
      if (v !== undefined && v !== null) p = p.set(k, String(v));
    });
    return p;
  }

  private mergeParams(
    globalService: any,
    filterParams: any,
    explicitParams: Record<string, string>
  ): HttpParams {
    const merged: Record<string, string> = {};
    const g = globalService || {};
    const f = filterParams || {};
    Object.keys(g).forEach((k) => {
      if (g[k] != null && g[k] !== '') merged[k] = String(g[k]);
    });
    Object.keys(f).forEach((k) => {
      if (f[k] != null && f[k] !== '') merged[k] = String(f[k]);
    });
    Object.keys(explicitParams).forEach((k) => {
      if (explicitParams[k] != null && explicitParams[k] !== '') merged[k] = explicitParams[k];
    });
    let p = new HttpParams();
    Object.keys(merged).forEach((k) => { p = p.set(k, merged[k]); });
    return p;
  }

  loadSummaryElements(
    baseUrl: string,
    ssoId: string,
    taxYear: string,
    scenario: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<SummaryElement[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params = this.mergeParams(globalService, filterParams, {
      action_code: 't8xhy5',
      sso_id: ssoId,
      tax_year: taxYear,
      scenario: scenario,
    });
    return this.http
      .get<{ callSuccess: string; jsonObject?: SummaryElement[]; errorMessage?: string }>(url, { params })
      .pipe(
        map((res) => {
          if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
          throw new Error(res.errorMessage || 'Failed to load summary elements');
        })
      );
  }

  loadJurisdictions(
    baseUrl: string,
    ssoId: string,
    taxYear: string,
    scenario: string,
    globalService: any = {},
    filterParams: any = {}
  ): Observable<Jurisdiction[]> {
    const url = `${baseUrl}/loadJsonObject`;
    const params = this.mergeParams(globalService, filterParams, {
      action_code: 'wo9we3',
      sso_id: ssoId,
      tax_year: taxYear,
      scenario: scenario,
    });
    return this.http
      .get<{ callSuccess: string; jsonObject?: Jurisdiction[]; errorMessage?: string }>(url, { params })
      .pipe(
        map((res) => {
          if (res.callSuccess === '1' && res.jsonObject) return res.jsonObject;
          throw new Error(res.errorMessage || 'Failed to load jurisdictions');
        })
      );
  }

  saveEntry(
    baseUrl: string,
    actionCode: string,
    clobData: any[],
    clobSettings: any[],
    filterParams: any,
    ssoId: string,
    globalService: any
  ): Observable<{ callSuccess: string; errorMessage?: string }> {
    const url = `${baseUrl}/saveJsonObject?action_code=${actionCode}`;
    let params = this.buildParams(globalService, filterParams, ssoId);
    params = params.set('clob_data', JSON.stringify(clobData));
    params = params.set('clob_settings', JSON.stringify(clobSettings));
    params = params.set('sso_id', ssoId);
    const body = params.toString();
    return this.http.post<{ callSuccess: string; errorMessage?: string }>(url, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
    });
  }
}
